import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type React from "react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipPopup, TooltipTrigger } from "@/components/ui/tooltip";
import type { NavItemDef } from "./nav-model";

/**
 * A single sidebar nav row: an active-rail Link styled for the deep-ink sidebar.
 * Active items get the `--nav-active-bg` fill, white text, and a 3px
 * `--nav-brand` rail at the left edge; idle items tint on hover.
 *
 * When `collapsed`, the label is visually hidden (`sr-only`, so the Link's
 * accessible name is unaffected) and a right-side Tooltip carries it instead —
 * the icon rail still needs a way to convey what each row links to.
 */
export function NavItem({
  item,
  active,
  onNavigate,
  collapsed = false,
}: {
  item: NavItemDef;
  active: boolean;
  onNavigate?: () => void;
  collapsed?: boolean;
}): React.ReactElement {
  const { t } = useTranslation("common");
  const slug = item.labelKey.split(".").pop() ?? item.labelKey;
  const label = t(item.labelKey);

  const link = (
    <Link
      aria-current={active ? "page" : undefined}
      className={cn(
        "relative flex h-[38px] items-center gap-2.5 rounded-[var(--r-sm)] text-sm font-medium tracking-[-0.005em] transition-colors",
        collapsed ? "justify-center px-0" : "px-3",
        active
          ? "bg-[var(--nav-active-bg)] font-semibold text-white"
          : "text-[var(--nav-ink-2)] hover:bg-[var(--nav-bg-2)] hover:text-[var(--nav-ink)]",
      )}
      data-test={`nav-item-${slug}`}
      onClick={onNavigate}
      to={item.to}
    >
      <span
        aria-hidden="true"
        className={cn(
          "absolute top-2 bottom-2 left-0 w-[3px] rounded",
          active ? "bg-[var(--nav-brand)]" : "bg-transparent",
        )}
      />
      <item.icon
        className={cn(
          "size-[18px] shrink-0",
          active ? "text-[var(--nav-brand)]" : "text-current",
        )}
      />
      <span className={cn("flex-1 truncate", collapsed && "sr-only")}>
        {label}
      </span>
    </Link>
  );

  if (!collapsed) {
    return link;
  }

  return (
    <Tooltip>
      <TooltipTrigger render={link} />
      <TooltipPopup side="right">{label}</TooltipPopup>
    </Tooltip>
  );
}
